/**
 * StartBodyScanButton — 3DLOOK Mobile Tailor Widget integration.
 *
 * ARCHITECTURE (widget path, active when REACT_APP_SAIA_PUBLIC_KEY is set):
 *   1. On click, inject <div class="saia-widget-container"></div> into the DOM.
 *   2. Register window.MTM_WIDGET_OPTIONS = { onMeasurementsReady: (data) => ... }
 *      BEFORE loading the script — the widget reads this on init.
 *   3. Inject the official <script src="https://mtm-widget.3dlook.me/integration.js" ...>
 *      with data-public-key = the tenant's PUBLIC key.
 *   4. Widget takes over the container, runs the scan flow in the customer's browser,
 *      uploads photos directly to SAIA, computes measurements, then fires the callback.
 *   5. We normalise the returned measurement object, POST to /api/body-profile with
 *      source='3dlook_widget', invoke onComplete(profile), and unmount cleanly.
 *
 * FALLBACK (legacy path, active when the public key is empty — dev / mock builds):
 *   Uses the previous /api/scans/initiate + /complete-mock flow so mock development
 *   keeps working without any real 3DLOOK credentials.
 *
 * SAFETY:
 *   - `submittedRef` guards against the widget firing onMeasurementsReady more than once.
 *   - `timeoutRef` aborts the scan after 5 min with a retry CTA.
 *   - Public API of this component (props + data-testid values) is unchanged from the
 *     previous version — parent flows (StylistFlow, MyFitProfile) don't need updates.
 *   - THREEDLOOK_MODE and Razorpay are NOT touched by this component.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Camera, X, Loader2, Sparkles, Check, ExternalLink, AlertTriangle, Chrome, Copy, ArrowUpRight } from 'lucide-react';
import { scanAPI, bodyProfileAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ingestWithRetry } from '../utils/scanIngestRetry';

const GOLD  = '#D4AF37';
const INK   = '#0F0F0F';
const MUTED = '#8a8a8a';
const GREEN = '#166534';
const RED   = '#991B1B';

const WIDGET_SCRIPT_ID = 'saia-mtm-integration';
const WIDGET_SCRIPT_SRC = 'https://mtm-widget.3dlook.me/integration.js';
const SCAN_TIMEOUT_MS = 5 * 60 * 1000; // 5 min

// ─── Environment guard ─────────────────────────────────────────────
// 3DLOOK's widget refuses to open its native camera flow inside an embedded
// browser (iframe / in-app browser / Android WebView) and shows a
// "Please continue in Chrome" QR screen. That gate is INSIDE the widget's
// own script — we can't disable it. What we CAN do is detect the same
// conditions BEFORE we load the widget, and show a clean LUNORA'S-branded
// "Open in Chrome" bail-out that hands the user off to their real browser.
//
// Detection is intentionally conservative — false positives just add one
// extra tap ("Continue anyway") and never hurt supported environments.
const detectEmbeddedContext = () => {
  if (typeof window === 'undefined') return { blocked: false, reason: null };
  let iframed = false;
  try { iframed = window.top !== window.self; } catch { iframed = true; } // cross-origin throw → treat as iframed
  const ua = navigator.userAgent || '';
  const inApp = /(FB_IAB|FBAN|FBAV|Instagram|Line|MicroMessenger|WeChat|WhatsApp|Twitter|LinkedIn|Snapchat|Pinterest|Slack|GSA)/i.test(ua);
  const androidWebView = /Android/i.test(ua) && /; wv\)/i.test(ua);
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  if (iframed && isMobile)  return { blocked: true, reason: 'iframe' };
  if (inApp)                return { blocked: true, reason: 'inapp' };
  if (androidWebView)       return { blocked: true, reason: 'webview' };
  // Non-mobile is left to the widget's own QR/desktop hand-off (by design).
  return { blocked: false, reason: null };
};

// Build a URL that reliably opens LUNORA'S in the user's default browser.
// On Android we prefer an `intent://` link which forces Chrome, then falls
// back to a normal https URL (window.open with _blank) for everyone else.
const buildOpenInChromeUrl = () => {
  const href = typeof window !== 'undefined' ? window.location.href : '';
  const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
  if (/Android/i.test(ua)) {
    try {
      const u = new URL(href);
      const hostPath = u.host + u.pathname + u.search + u.hash;
      return `intent://${hostPath}#Intent;scheme=https;package=com.android.chrome;end`;
    } catch { /* fall through */ }
  }
  return href;
};

// ─── Normaliser ─────────────────────────────────────────────────
// The Mobile Tailor widget's response shape isn't publicly documented field-by-field.
// We map every numeric field we see into our canonical schema and preserve unknown
// keys under `raw` so nothing is silently lost.
const CANONICAL_MAP = {
  // Common SAIA REST names → canonical
  chest_girth: 'chest', bust_girth: 'bust', under_bust_girth: 'under_bust',
  waist_girth: 'waist', hips_girth: 'hip', high_hips_girth: 'hip',
  shoulder_width: 'shoulder', sleeve_length: 'sleeve_length',
  arm_length: 'arm_length', inseam_length: 'inseam',
  outseam_length: 'outseam', thigh_girth: 'thigh',
  knee_girth: 'knee', ankle_girth: 'ankle', neck_girth: 'neck',
  armhole_girth: 'armhole', back_length: 'back_length',
  // Widget may also return short names directly
  chest: 'chest', bust: 'bust', waist: 'waist', hip: 'hip',
  shoulder: 'shoulder', neck: 'neck', armhole: 'armhole',
  torso: 'torso', inseam: 'inseam', outseam: 'outseam',
  thigh: 'thigh', knee: 'knee', ankle: 'ankle',
  front_neck: 'front_neck', back_neck: 'back_neck',
};

const normaliseWidgetPayload = (raw) => {
  const source = raw?.measurements || raw?.result || raw || {};
  const measurements = {};
  const unmapped = {};
  Object.entries(source).forEach(([k, v]) => {
    if (typeof v !== 'number' && !(v && typeof v === 'object' && 'value' in v)) return;
    const numeric = typeof v === 'number' ? v : v.value;
    if (typeof numeric !== 'number' || !isFinite(numeric)) return;
    const canonical = CANONICAL_MAP[k] || CANONICAL_MAP[k.toLowerCase()];
    if (canonical) {
      measurements[canonical] = Math.round(numeric * 10) / 10; // 1 decimal, cm
    } else {
      unmapped[k] = numeric;
    }
  });
  return {
    measurements,
    unmapped,
    height_cm: raw?.height_cm || raw?.height || null,
    weight_kg: raw?.weight_kg || raw?.weight || null,
    gender: raw?.gender || null,
    person_id: raw?.person_id || raw?.id || null,
    confidence: raw?.confidence || null,
  };
};

// ─── Component ─────────────────────────────────────────────────
const StartBodyScanButton = ({ onComplete, testId = 'start-body-scan', secondary = false, disabled = false }) => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const customerEmail = user?.email || '';
  const publicKey = process.env.REACT_APP_SAIA_PUBLIC_KEY || '';
  const widgetMode = publicKey.length > 0; // true → use widget; false → legacy mock flow

  const [open, setOpen]     = useState(false);
  const [status, setStatus] = useState('idle'); // idle|starting|scanning|submitting|done|error|timeout|embedded
  const [error, setError]   = useState(null);
  const [scan, setScan]     = useState(null);   // legacy-mode scan record (unused in widget mode)
  const [copied, setCopied] = useState(false);

  const env = useMemo(detectEmbeddedContext, []);

  const submittedRef = useRef(false);
  const pendingIngestRef = useRef(null); // person_id still processing on 3DLOOK — retried, never re-scanned
  const timeoutRef = useRef(null);
  const pollRef = useRef(null);
  const scriptRef = useRef(null);

  // ─── Cleanup helpers ─────────────────────────
  const clearTimers = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (pollRef.current)   { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const removeWidgetScript = useCallback(() => {
    // Remove the injected script + reset global options so a subsequent open starts clean.
    if (scriptRef.current && scriptRef.current.parentNode) {
      scriptRef.current.parentNode.removeChild(scriptRef.current);
    }
    scriptRef.current = null;
    if (typeof window !== 'undefined') {
      try { delete window.MTM_WIDGET_OPTIONS; } catch { window.MTM_WIDGET_OPTIONS = undefined; }
    }
  }, []);

  useEffect(() => () => { clearTimers(); removeWidgetScript(); }, [clearTimers, removeWidgetScript]);

  // ─── Submit measurements to our backend ─────
  // AUTHORITATIVE PATH: forward ONLY the SAIA person_id / taskset_id from
  // the widget callback. The backend calls SAIA's GET /api/v2/persons/{id}/
  // server-side (with the secret API key) to retrieve the real measurement
  // data, so we never trust client-side numbers.
  //
  // LEGACY FALLBACK: if the widget callback does not include a person_id
  // (e.g. during mock/local development, or an older widget build), fall
  // back to the previous direct-save path so nothing breaks. This is only
  // reachable when we cannot construct an authoritative fetch.
  const submitMeasurements = useCallback(async (raw) => {
    if (submittedRef.current) return;   // dedup — widget sometimes fires twice
    submittedRef.current = true;

    const { measurements, unmapped, height_cm, weight_kg, gender, person_id, confidence } = normaliseWidgetPayload(raw);
    const taskset_id = raw?.taskset_id || raw?.tasksetId || raw?.task_set_id || null;

    // Safe shape log — keys only, no measurement values (values are personal data).
    try {
      console.log('[3DLOOK widget] callback received.',
        'person_id present:', !!person_id,
        '| taskset_id present:', !!taskset_id,
        '| known keys:', Object.keys(measurements),
        '| unmapped keys:', Object.keys(unmapped));
    } catch {/* noop */}

    setStatus('submitting');

    // ── AUTHORITATIVE PATH ──
    if (person_id) {
      clearTimers(); // scan finished — the 5-min widget timeout must not fire during ingest retries
      pendingIngestRef.current = { person_id: String(person_id), taskset_id: taskset_id ? String(taskset_id) : undefined };
      try {
        const profile = await ingestWithRetry(() => bodyProfileAPI.fromWidget(pendingIngestRef.current));
        pendingIngestRef.current = null;
        try { localStorage.removeItem('lunora_scan_session_v1'); } catch {/* noop */}
        setStatus('done');
        clearTimers();
        setTimeout(() => {
          setOpen(false);
          removeWidgetScript();
          onComplete && onComplete(profile);
        }, 900);
        return;
      } catch (e) {
        // 202 = still processing on SAIA side; surface a distinct message and let user retry.
        const msg = e?.message || 'Could not save scan';
        const isProcessing = e?.code === 'SCAN_STILL_PROCESSING';
        if (!isProcessing) pendingIngestRef.current = null; // only a still-processing scan is retried as-is
        setStatus(isProcessing ? 'timeout' : 'error');
        setError(isProcessing
          ? 'Your measurements are still being computed. Please tap Retry in a few seconds.'
          : msg);
        submittedRef.current = false; // allow retry
        return;
      }
    }

    // ── LEGACY / MOCK FALLBACK — no person_id from widget ──
    if (!Object.keys(measurements).length) {
      setStatus('error');
      setError('Scan finished but no scan identifier was returned. Please retry.');
      submittedRef.current = false;
      return;
    }
    try {
      const payload = {
        measurements,
        height_cm: typeof height_cm === 'number' ? height_cm : 165,
        weight_kg: typeof weight_kg === 'number' ? weight_kg : 60,
        gender: (gender === 'male' || gender === 'female' || gender === 'unisex') ? gender : 'female',
        source: '3dlook_widget',
        three_d_look_scan_id: null,
        confidence: confidence || {},
        raw_widget_keys_unmapped: Object.keys(unmapped),
      };
      const profile = await bodyProfileAPI.save(payload);
      setStatus('done');
      clearTimers();
      setTimeout(() => {
        setOpen(false);
        removeWidgetScript();
        onComplete && onComplete(profile);
      }, 900);
    } catch (e) {
      setStatus('error');
      setError(e.message || 'Could not save scan');
      submittedRef.current = false; // allow retry
    }
  }, [clearTimers, removeWidgetScript, onComplete]);

  // ─── Widget launcher ────────────────────────
  const launchWidget = useCallback(async () => {
    if (!widgetMode) return;
    setStatus('scanning');
    // RECOVERY-BEFORE-LAUNCH — persist a scan-session token so that if the
    // widget hands off to an external Chrome tab / 3dl.ink flow (in which
    // case our onMeasurementsReady callback will NEVER fire), StylistFlow
    // can still recover the person_id on return by asking SAIA which persons
    // were created for this account since this timestamp.
    try {
      const s = await bodyProfileAPI.startScanSession();
      localStorage.setItem('lunora_scan_session_v1', JSON.stringify({
        session_token: s.session_token,
        created_at: s.created_at,
        ts: Date.now(),
      }));
    } catch (e) {
      // Stale login (session expired) — scans must belong to a signed-in customer.
      if (e?.status === 401) {
        setOpen(false); setStatus('idle');
        await checkAuth(); // clears the stale user so /auth shows the login form
        navigate('/auth', { state: { redirectTo: location.pathname,
          redirectState: { ...(location.state || {}), resumeStep: 'scan' } } });
        return;
      }
    }
    // Register callback BEFORE loading the script — the widget reads this on init.
    // returnUrl is set explicitly so that IF 3DLOOK's Mobile-Tailor hand-off
    // flow redirects the user back after an external scan, they land on our
    // /measurements page. Kate confirmed (2026-09-11) that on redirect the
    // widget appends `?measurements=true&id=<PERSON_ID>&uuid=<FLOW_UUID>` —
    // StylistFlow's Layer A reads those keys and ingests the exact scan.
    //
    // The primary deterministic path remains onMeasurementsReady in the same
    // tab; returnUrl is the fallback when the scan completes cross-context.
    const returnUrl = (typeof window !== 'undefined'
                       && window.location && window.location.origin)
      ? `${window.location.origin}/measurements`
      : undefined;
    // Kate confirmed (2026-09-11): pre-filling defaultValues.email + disabling
    // the input and screen ties every scan to the LUNORA'S customer's email.
    // This is what powers the webhook update_customer → update_person linkage.
    const emailDefaults = customerEmail
      ? {
          defaultValues: { email: customerEmail },
          disableInput:  { email: true },
          disableScreen: { email: true },
        }
      : {};
    window.MTM_WIDGET_OPTIONS = {
      ...emailDefaults,
      returnUrl,
      onMeasurementsReady: (data) => {
        try { submitMeasurements(data); }
        catch (e) { setStatus('error'); setError(e.message || 'Callback failed'); }
      },
    };
    // Timeout safety
    timeoutRef.current = setTimeout(() => {
      if (!submittedRef.current) {
        setStatus('timeout');
        setError('Scan took too long. Please retry when you have a stable connection.');
        removeWidgetScript();
      }
    }, SCAN_TIMEOUT_MS);
    // Inject widget script
    const existing = document.getElementById(WIDGET_SCRIPT_ID);
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    const s = document.createElement('script');
    s.id = WIDGET_SCRIPT_ID;
    s.async = true;
    s.src = WIDGET_SCRIPT_SRC;
    s.setAttribute('data-public-key', publicKey);
    s.onerror = () => {
      setStatus('error');
      setError('Could not load the body scan. Check your network and retry.');
    };
    document.body.appendChild(s);
    scriptRef.current = s;
  }, [widgetMode, publicKey, submitMeasurements, removeWidgetScript, customerEmail, navigate, location, checkAuth]);

  // ─── Legacy mock launcher (unchanged behaviour) ─
  const launchLegacyMock = useCallback(async () => {
    setStatus('starting'); setError(null);
    try {
      const r = await scanAPI.initiate({});
      setScan(r);
      setStatus('scanning');
      pollRef.current = setInterval(async () => {
        try {
          const s = await scanAPI.status(r.scan_id);
          if (s.status === 'completed') {
            clearTimers();
            setStatus('done');
            setTimeout(() => { setOpen(false); onComplete && onComplete(s); }, 800);
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      setStatus('error');
      setError(e.message || 'Could not start scan');
    }
  }, [clearTimers, onComplete]);

  const handleMockComplete = useCallback(async () => {
    if (!scan) return;
    try {
      await scanAPI.completeMock(scan.scan_id);
      // Polling will pick it up shortly; nothing to do here.
    } catch (e) {
      setStatus('error');
      setError(e.message || 'Mock completion failed');
    }
  }, [scan]);

  // ─── Main entry point ──────────────────────
  // Re-check the SAME 3DLOOK person (no new scan) after a still-processing timeout.
  const retryIngest = async () => {
    setStatus('submitting'); setError('');
    try {
      const profile = await ingestWithRetry(() => bodyProfileAPI.fromWidget(pendingIngestRef.current));
      pendingIngestRef.current = null;
      try { localStorage.removeItem('lunora_scan_session_v1'); } catch {/* noop */}
      setStatus('done');
      setTimeout(() => { setOpen(false); removeWidgetScript(); onComplete && onComplete(profile); }, 900);
    } catch (e) {
      const isProcessing = e?.code === 'SCAN_STILL_PROCESSING';
      if (!isProcessing) pendingIngestRef.current = null;
      setStatus(isProcessing ? 'timeout' : 'error');
      setError(e?.message || 'Could not save scan');
    }
  };

  const handleStart = async () => {
    // Scans must be bound to an authenticated customer — verify the live session, not cached state.
    if (!(await checkAuth())) {
      navigate('/auth', { state: { redirectTo: location.pathname,
        redirectState: { ...(location.state || {}), resumeStep: 'scan' } } });
      return;
    }
    submittedRef.current = false;
    setError(null); setScan(null); setCopied(false);
    setOpen(true);
    // PRE-FLIGHT GUARD — if we're inside Emergent Preview / an in-app browser /
    // an Android WebView, the 3DLOOK widget will refuse to launch its native
    // camera flow. Short-circuit here and show LUNORA'S own "Open in Chrome"
    // hand-off screen instead. Never inject the widget script in this branch.
    if (widgetMode && env.blocked) {
      setStatus('embedded');
      return;
    }
    if (widgetMode) {
      // Give React a beat to render the container before injecting the script.
      setTimeout(() => launchWidget(), 50);
    } else {
      launchLegacyMock();
    }
  };

  const handleForceLaunch = () => {
    // User overrode the embedded-context guard ("Continue anyway"). Load the
    // widget in place — it will either work or show its own gate.
    setStatus('starting');
    setTimeout(() => launchWidget(), 50);
  };

  const handleOpenInChrome = () => {
    const target = buildOpenInChromeUrl();
    // On Android, the intent:// URL forces Chrome. window.open with _blank is
    // the reliable escape from an iframe/in-app browser (cross-origin
    // top-level location.href writes are blocked by the browser).
    try { window.open(target, '_blank', 'noopener'); }
    catch { window.location.href = target; }
  };

  const handleCopyLink = async () => {
    const href = window.location.href;
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: temp input + execCommand for older in-app browsers.
      const el = document.createElement('input');
      el.value = href; document.body.appendChild(el); el.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); }
      finally { document.body.removeChild(el); }
    }
  };

  const handleClose = () => {
    clearTimers();
    removeWidgetScript();
    setOpen(false);
    setTimeout(() => {
      setScan(null); setStatus('idle'); setError(null); submittedRef.current = false;
    }, 300);
  };

  const btnStyle = secondary
    ? { border: `1px solid ${INK}`, color: INK, background: '#FFF' }
    : { background: 'linear-gradient(135deg, #1A1A1A 0%, #D4AF37 140%)', color: '#FFF' };

  return (
    <>
      <button
        data-testid={testId}
        onClick={handleStart}
        disabled={disabled || status === 'starting'}
        className="w-full py-3 flex items-center justify-center gap-2 text-[11px] tracking-[0.18em] uppercase transition active:scale-[0.99] disabled:opacity-60"
        style={{ ...btnStyle, fontWeight: 500 }}>
        {status === 'starting'
          ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.6} />
          : <Camera className="w-4 h-4" strokeWidth={1.6} />}
        Start Body Scan
      </button>

      {open && (
        <div data-testid="scan-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full sm:max-w-md bg-white overflow-hidden"
            style={{ borderTopLeftRadius: 18, borderTopRightRadius: 18, maxHeight: '92vh' }}>
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: '#E5E5E5' }}>
              <div>
                <p className="text-[9px] tracking-[0.22em] uppercase" style={{ color: GOLD, fontWeight: 700 }}>
                  {widgetMode ? 'AI Body Scan' : 'Body Scan · Preview Mode'}
                </p>
                <h2 style={{ fontFamily: 'Playfair Display, serif', color: INK, fontSize: 18, fontWeight: 500 }}>
                  Body Scan
                </h2>
              </div>
              <button data-testid="close-scan-modal" onClick={handleClose} className="p-1.5 -mr-1.5">
                <X className="w-5 h-5" style={{ color: MUTED }} strokeWidth={1.5} />
              </button>
            </div>

            {/* Body */}
            <div className="px-4 py-5" data-testid="scan-modal-body">
              {status === 'embedded' && (
                <div data-testid="scan-embedded-guard">
                  <div className="text-center pt-2 pb-4">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center"
                      style={{ background: 'rgba(212,175,55,0.08)', border: `1px solid ${GOLD}`, borderRadius: 999 }}>
                      <Chrome className="w-5 h-5" style={{ color: GOLD }} strokeWidth={1.5} />
                    </div>
                    <p style={{ fontFamily: 'Playfair Display, serif', color: INK, fontSize: 18, fontWeight: 500, letterSpacing: '-0.01em' }}>
                      Open LUNORA&apos;S in your browser
                    </p>
                    <p className="text-[11.5px] mt-1.5" style={{ color: MUTED, lineHeight: 1.55 }}>
                      For the AI Body Scan, please continue in your default
                      browser so your camera and motion sensors work correctly.
                    </p>
                  </div>

                  <button
                    data-testid="open-in-chrome-btn"
                    onClick={handleOpenInChrome}
                    className="w-full py-3 mb-2 text-[11px] tracking-[0.18em] uppercase text-white flex items-center justify-center gap-2 active:scale-[0.99]"
                    style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #D4AF37 140%)', fontWeight: 500 }}>
                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
                    Open in Chrome
                  </button>

                  <button
                    data-testid="copy-link-btn"
                    onClick={handleCopyLink}
                    className="w-full py-2.5 mb-3 text-[10.5px] tracking-[0.14em] uppercase flex items-center justify-center gap-2 active:scale-[0.99]"
                    style={{ border: `1px solid ${INK}`, color: INK, background: '#FFF', fontWeight: 500 }}>
                    {copied
                      ? <><Check className="w-3.5 h-3.5" strokeWidth={1.8} /> Link copied</>
                      : <><Copy className="w-3.5 h-3.5" strokeWidth={1.6} /> Copy link</>}
                  </button>

                  <div className="text-center">
                    <button
                      data-testid="scan-continue-anyway"
                      onClick={handleForceLaunch}
                      className="text-[10px] tracking-[0.14em] uppercase underline"
                      style={{ color: MUTED }}>
                      Continue anyway
                    </button>
                  </div>

                  <div className="mt-4 p-3" style={{ background: '#FAF9F6', border: '1px solid #E5E5E5' }}>
                    <p className="text-[9.5px] tracking-[0.12em] uppercase mb-1" style={{ color: MUTED, fontWeight: 700 }}>
                      Why?
                    </p>
                    <p className="text-[10.5px]" style={{ color: MUTED, lineHeight: 1.55 }}>
                      You appear to be viewing LUNORA&apos;S inside an embedded
                      browser {env.reason === 'inapp' ? '(social app / in-app view)'
                                : env.reason === 'webview' ? '(app WebView)'
                                : '(preview frame)'}. The AI Body Scan needs
                      full camera + motion-sensor permissions that only a real
                      Chrome or Safari tab can grant.
                    </p>
                  </div>
                </div>
              )}

              {(status === 'starting') && (
                <div className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: GOLD }} strokeWidth={1.5} />
                  <p className="text-[11px] mt-2" style={{ color: MUTED }}>Starting body scan…</p>
                </div>
              )}

              {(status === 'submitting') && (
                <div className="text-center py-8" data-testid="scan-submitting">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: GOLD }} strokeWidth={1.5} />
                  <p className="text-[11px] mt-2" style={{ color: MUTED }}>Saving your measurements…</p>
                </div>
              )}

              {(status === 'error' || status === 'timeout') && (
                <div className="text-center py-6" data-testid="scan-error">
                  <AlertTriangle className="w-6 h-6 mx-auto mb-2" style={{ color: RED }} strokeWidth={1.5} />
                  <p className="text-[12px]" style={{ color: RED }}>{error || 'Something went wrong'}</p>
                  <button onClick={pendingIngestRef.current ? retryIngest : handleStart}
                    data-testid="scan-retry"
                    className="mt-3 text-[10px] tracking-[0.12em] uppercase underline" style={{ color: RED }}>
                    Try Again
                  </button>
                </div>
              )}

              {status === 'done' && (
                <div className="text-center py-6" data-testid="scan-done">
                  <div className="w-10 h-10 rounded-full mx-auto flex items-center justify-center" style={{ background: GREEN }}>
                    <Check className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                  <p className="text-[13px] mt-3" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>
                    Scan completed
                  </p>
                  <p className="text-[10.5px] mt-1" style={{ color: MUTED }}>
                    Applying to your fit profile…
                  </p>
                </div>
              )}

              {/* WIDGET mode: mount container. Widget replaces it with its own UI. */}
              {status === 'scanning' && widgetMode && (
                <div data-testid="saia-widget-wrapper">
                  <div className="saia-widget-container" data-testid="saia-widget-container"
                    style={{ minHeight: '58vh' }} />
                  <p className="text-[10.5px] text-center mt-2" style={{ color: MUTED }}>
                    <Loader2 className="w-2.5 h-2.5 inline animate-spin mr-1" strokeWidth={1.6} />
                    Follow the on-screen guidance to capture your scan.
                  </p>
                </div>
              )}

              {/* LEGACY MOCK mode: unchanged from previous version */}
              {status === 'scanning' && !widgetMode && scan && (
                <div data-testid="mock-scan-panel">
                  <div className="p-3 mb-3"
                    style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.35)' }}>
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-3.5 h-3.5 mt-0.5" style={{ color: GOLD }} strokeWidth={1.6} />
                      <div>
                        <p className="text-[10px] tracking-[0.16em] uppercase" style={{ color: GOLD, fontWeight: 700 }}>
                          Developer Mode
                        </p>
                        <p className="text-[11px] mt-1" style={{ color: INK, lineHeight: 1.5 }}>
                          Body-scan provider not configured. Using realistic mocked measurements
                          so the app flow works end-to-end.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mb-3 p-3" style={{ background: '#FAF9F6', border: '1px solid #E5E5E5' }}>
                    <p className="text-[9px] tracking-[0.14em] uppercase mb-1" style={{ color: MUTED, fontWeight: 700 }}>
                      Widget URL
                    </p>
                    <a href={scan.mobile_tailor_url} target="_blank" rel="noreferrer"
                      className="text-[10.5px] break-all flex items-start gap-1" style={{ color: GOLD }}>
                      {scan.mobile_tailor_url}
                      <ExternalLink className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" strokeWidth={1.8} />
                    </a>
                    <p className="text-[9.5px] mt-1" style={{ color: MUTED }}>
                      Scan ID: {scan.scan_id?.slice(0, 8)}… · Person ID: {scan.saia_person_id?.slice(0, 12)}…
                    </p>
                  </div>
                  <button
                    data-testid="simulate-scan-complete"
                    onClick={handleMockComplete}
                    className="w-full py-3 text-[11px] tracking-[0.18em] uppercase text-white flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #D4AF37 140%)', fontWeight: 500 }}>
                    <Sparkles className="w-4 h-4" strokeWidth={1.6} />
                    Simulate Scan Complete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StartBodyScanButton;
